/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Caseworker dialog that adds ONE signer to an existing signing process through the
 * dedicated endpoint, capturing name, profile role, channel variant and the delivery addresses
 * together with the person anchor they belong to.
 */
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {SmSigningApiService} from '../../service/api/sm-signing-api.service';
import {SmSignatureProfileRoleService} from '../../service/api/sm-signature-profile-role.service';
import {SmSignatureProfileRoleDto} from '../../model/dto/entity/sm-signature-profile-role.dto';
import {SmChannelTypeDto} from '../../model/dto/entity/sm-channel-type.dto';
import {SmSignerResponseDto} from '../../model/dto/api/sm-signer-response.dto';
import {SmAddSignerRequestDto} from '../../model/dto/api/sm-add-signer-request.dto';
import {SmSignerAddressCapture} from '../../interface/sm-signer-address-capture.interface';
import {ObjectRequestList} from '../../../../core/shared/dto/object/request/object-request-list';
import {FilterCriteria} from '../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../core/shared/misc/sorting';

/**
 * "Unterzeichner hinzufügen".
 *
 * <h2>Why this exists instead of the generic metadata form</h2>
 * <p>
 * The signer table's own create action opened the platform's metadata form against
 * {@code sm.SmSigningProcessSigner}, whose registered route has no controller behind it in the
 * backend. The form's template request answered 404, so the dialog opened with a header and NO
 * FIELDS — a surface that looks like a defect, cannot be completed, and gives no clue why. Over the
 * whole external signing route there was therefore no way at all to add a signer from the interface.
 * </p>
 *
 * <h2>The endpoint it writes to is the better one, not merely the available one</h2>
 * <p>
 * A CRUD form would write the signer row and stop there. {@code POST .../signers} writes the row AND
 * captures the person anchor, and DERIVES both address origins from that anchor rather than
 * accepting them from the request. The origin is the fact the audit trail and the evidence level
 * rest on; a form that let it be typed would make it a matter of assertion. That is also why the
 * dialog captures the addresses through {@code mvs-sm-signer-address-capture} rather than with two
 * plain inputs — the capture's whole point is that the origin follows from the PATH taken.
 * </p>
 *
 * <h2>The role is a KEY and it is matched verbatim</h2>
 * <p>
 * {@code SmSignatureProfileRole.roleKey} is compared character for character against the part of the
 * PDF's signature field name in front of the separator, umlauts and casing included. So the control
 * offers the profile's configured roles and sends the key while showing the caption — a free-text
 * field would invite a role that matches nothing in the document, and a signer whose role matches
 * nothing has no signature place to fill. Where the process carries no profile there is no list to
 * offer, and the field falls back to free text with that stated.
 * </p>
 *
 * <h2>It refuses nothing itself beyond the name</h2>
 * <p>
 * Every other rule — the terminal process status, the channel variant the document type does not
 * release, an unknown variant — is the backend's, and it answers each in its own German sentence
 * naming which rule refused. Those sentences are rendered verbatim. A client-side guess would be a
 * claim about a cause this component does not hold.
 * </p>
 */
@Component({
    selector: 'mvs-sm-signer-create-dialog',
    templateUrl: './sm-signer-create-dialog.component.html',
    styleUrls: ['./sm-signer-create-dialog.component.scss'],
    standalone: false
})
export class SmSignerCreateDialogComponent implements OnInit, OnChanges, OnDestroy {

    /** The process the signer is added to. */
    @Input() processId: number;

    /**
     * The signature profile of that process. Drives both the role list and the released channel
     * variants; absent, the dialog still works and says what is missing.
     */
    @Input() signatureProfileId: number;

    /** Pins the object type of the person anchor. Left unset, the capture offers the type chooser. */
    @Input() personObjectTypeAlias: string;

    /** Whether the dialog is on screen. Two-way through {@link visibleChange}. */
    @Input() visible: boolean = false;

    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    /** The signer that was created. The host reloads its own state from this. */
    @Output() onSignerCreated: EventEmitter<SmSignerResponseDto> =
        new EventEmitter<SmSignerResponseDto>();

    initialized: boolean = false;

    /** The roles of the profile, offered as the role choice. */
    roles: SmSignatureProfileRoleDto[] = [];
    rolesLoading: boolean = false;
    rolesLoadError: string = null;

    /** Whether the profile has been read at least once, so an empty list can be told from an unread one. */
    rolesLoaded: boolean = false;

    // --- The form ---

    signerName: string = null;
    signerRole: string = null;
    signingOrder: number = 1;
    mandatory: boolean = true;

    /** The chosen variant, whole — the capture below needs its two behaviour flags. */
    selectedChannelType: SmChannelTypeDto = null;

    /** What the address capture last produced: addresses, anchor, and whether it is complete. */
    capture: SmSignerAddressCapture = null;

    saving: boolean = false;

    /** The server's own sentence when it refused. Rendered verbatim. */
    saveError: string = null;

    private subscription: Subscription = new Subscription();

    private roleSubscription: Subscription = null;

    /** Which profile the role list belongs to, so an unchanged profile is not read twice. */
    private loadedProfileId: number = null;

    constructor(
        private signingApiService: SmSigningApiService,
        private profileRoleService: SmSignatureProfileRoleService,
        private cdr: ChangeDetectorRef
    ) {
    }

    /** Adopts the inputs and starts the read. Kept apart from the hook, as the module convention has it. */
    initComponent(): void {
        if (this.signatureProfileId) {
            this.loadRoles();
        }
    }

    ngOnInit(): void {
        this.initComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes['signatureProfileId']) {
            this.roles = [];
            this.rolesLoaded = false;
            this.loadedProfileId = null;
            if (this.signatureProfileId) {
                this.loadRoles();
            }
        }

        // Opening the dialog starts a NEW signer. Carrying the previous one's name, role and captured
        // person over would let a second signer be created against the first one's anchor by pressing
        // the button twice — the one mistake this surface can make that nothing on screen would show.
        if (changes['visible'] && this.visible && !changes['visible'].firstChange) {
            this.resetForm();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Reads the roles of the signature profile. Also the recovery from a failed load.
     *
     * <p>
     * Sorted by the configured signing order rather than by name, because that order is what the
     * profile means by first and second signer, and the number the dialog proposes is derived from
     * the position the caseworker picks.
     * </p>
     */
    loadRoles(): void {

        if (!this.signatureProfileId) {
            return;
        }

        if (this.loadedProfileId === this.signatureProfileId && this.rolesLoaded) {
            return;
        }

        this.rolesLoading = true;
        this.rolesLoadError = null;
        this.loadedProfileId = this.signatureProfileId;

        const request: ObjectRequestList = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create('profile', FilterCriteria.cOperatorEqual, this.signatureProfileId)],
            [new Sorting('signingOrder', true)]
        );

        // Replaced rather than added to the bag: a profile that changes twice would otherwise leave
        // the first read's subscription alive and let a stale list win the race.
        this.roleSubscription?.unsubscribe();
        this.roleSubscription = this.profileRoleService.list(request).subscribe({
            next: (result: { entries: SmSignatureProfileRoleDto[] }) => {
                this.roles = result?.entries || [];
                this.rolesLoading = false;
                this.rolesLoaded = true;
                this.cdr.detectChanges();
            },
            error: () => {
                this.rolesLoading = false;
                this.rolesLoaded = true;
                this.rolesLoadError = 'Die Rollen des Signaturprofils konnten nicht geladen werden. '
                    + 'Der Rollenschlüssel kann so lange von Hand eingetragen werden.';
                this.cdr.detectChanges();
            }
        });
        this.subscription.add(this.roleSubscription);
    }

    /** The caption of one role — never the key, which is a matching token and not a label. */
    roleLabel(role: SmSignatureProfileRoleDto): string {
        return role?.name?.trim() ? role.name : (role?.roleKey || '');
    }

    /** Whether the role has to be typed because no list could be offered. */
    get roleIsFreeText(): boolean {
        return !this.signatureProfileId || (this.rolesLoaded && this.roles.length === 0)
            || !!this.rolesLoadError;
    }

    /** The variant the caseworker chose; the address capture reads its two behaviour flags. */
    handleChannelTypeChanged(channelType: SmChannelTypeDto): void {

        this.selectedChannelType = channelType;

        // The capture is bound to the variant: a variant that no longer asks for a mobile number
        // leaves a number captured for the previous one standing, and it would travel with the
        // request under an origin derived for a channel that is no longer in play.
        this.capture = null;
    }

    /** What the capture last produced. Held whole, because {@code valid} belongs to the same answer. */
    handleCaptureChanged(capture: SmSignerAddressCapture): void {
        this.capture = capture;
    }

    /**
     * Whether the dialog may be submitted.
     *
     * <p>
     * The name is the one field this component insists on — it is the only one the backend refuses
     * outright, and the refusal explains that without a name no delivery address can be determined.
     * The capture's own {@code valid} is honoured where a variant was chosen; where none was, the
     * signer is created without a delivery route, which is exactly what an in-person signer is.
     * </p>
     */
    get submittable(): boolean {

        if (this.saving || !this.processId) {
            return false;
        }

        if (!this.signerName || !this.signerName.trim()) {
            return false;
        }

        if (this.selectedChannelType && !this.capture?.valid) {
            return false;
        }

        return true;
    }

    /** Sends the signer. The origins are derived server-side and are deliberately not sent. */
    save(): void {

        if (!this.submittable) {
            return;
        }

        this.saving = true;
        this.saveError = null;

        const request: SmAddSignerRequestDto = {
            signerName: this.signerName.trim(),
            signerRole: this.signerRole?.trim() || null,
            signingOrder: this.signingOrder ?? 1,
            mandatory: this.mandatory,
            signerEmail: this.capture?.signerEmail || null,
            mobileNumber: this.capture?.mobileNumber || null,
            signerObjectType: this.capture?.signerObjectType || null,
            signerObjectId: this.capture?.signerObjectId ?? null,
            channelId: this.selectedChannelType?.id ?? null
        };

        this.subscription.add(
            this.signingApiService.addSigner(this.processId, request).subscribe({
                next: (signer: SmSignerResponseDto) => {
                    this.saving = false;
                    this.onSignerCreated.emit(signer);
                    this.close();
                    this.cdr.detectChanges();
                },
                error: (err: HttpErrorResponse) => {
                    this.saving = false;
                    this.saveError = this.extractErrorText(err,
                        'Der Unterzeichner konnte nicht angelegt werden.');
                    this.cdr.detectChanges();
                }
            })
        );
    }

    /** Closes the dialog without writing anything. */
    cancel(): void {
        this.close();
    }

    /** Closes and tells the host, so its own flag follows. */
    private close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    /** Empties the form for the next signer. */
    private resetForm(): void {
        this.signerName = null;
        this.signerRole = null;
        this.signingOrder = 1;
        this.mandatory = true;
        this.selectedChannelType = null;
        this.capture = null;
        this.saveError = null;
    }

    /**
     * The server's own sentence, where it sent one.
     *
     * <p>
     * The sm controllers answer their refusals as a plain German string in the body, and Spring's own
     * problem detail puts it under {@code detail}. Both are tried before the fallback, because the
     * fallback says only that something failed and every one of those sentences says WHICH rule
     * refused and who can lift it.
     * </p>
     */
    private extractErrorText(err: HttpErrorResponse, fallback: string): string {

        const body: any = err?.error;

        if (typeof body === 'string' && body.trim()) {
            return body;
        }

        if (typeof body?.detail === 'string' && body.detail.trim()) {
            return body.detail;
        }

        if (typeof body?.message === 'string' && body.message.trim()) {
            return body.message;
        }

        return fallback;
    }
}
